import { DesktopWindowsOutlined, PhoneIphoneOutlined } from '@mui/icons-material'
import { Stack, Typography } from '@mui/material'
import { useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { FlexBox } from 'components/Box'
import { BoxForm, NumberField, TagsField } from 'components/Form'
import Switch from 'components/Form/Switch'
import { useAbility } from 'providers/casl'
import { CONTAINER_SPACING } from 'providers/material-ui/theme/constants'
import { useTooltips } from '../hooks/useTooltips'
import { Schema } from '../schema'

export const AdHolding = (): JSX.Element => {
  const ability = useAbility()
  const { t } = useTranslation('features/domain', {
    keyPrefix: 'form.viewabilityTools.basicSettings.adHolding',
  })
  const { t: tc } = useTranslation('common')
  const { control, getValues } = useFormContext<Schema>()

  const { adHoldingEnabled, excludeCreativeTypesEnabled } = getValues()

  const tooltips = useTooltips()
  const cannotUpdateDomain: boolean = ability.cannot('update', 'Domain')

  return (
    <>
      <Switch
        control={control}
        disabled={cannotUpdateDomain}
        label={t('label')}
        labelPlacement='start'
        name={'adHoldingEnabled'}
        tooltip={tooltips.adHolding}
      />

      {adHoldingEnabled && (
        <BoxForm sx={{ ml: CONTAINER_SPACING }}>
          <Stack
            direction='row'
            spacing={8}
          >
            <BoxForm>
              <FlexBox
                axis='x'
                sx={{ gap: 1 }}
              >
                <DesktopWindowsOutlined
                  data-cy='desktop-windows-outlined-icon'
                  fontSize='inherit'
                />
                <Typography>{tc('device.desktop')}</Typography>
              </FlexBox>

              <NumberField
                control={control}
                disabled={cannotUpdateDomain}
                fixedHelperText
                label={t('distanceFromPageHeader.label')}
                name='distanceFromPageHeaderDesktop'
                tooltip={{
                  content: t('distanceFromPageHeader.tooltip'),
                }}
                width='md'
              />

              <NumberField
                control={control}
                disabled={cannotUpdateDomain}
                fixedHelperText
                label={t('extraSpaceBelowAd.label')}
                name='extraSpaceBelowAdDesktop'
                tooltip={{
                  content: t('extraSpaceBelowAd.tooltip'),
                }}
                width='md'
              />
            </BoxForm>

            <BoxForm>
              <FlexBox
                axis='x'
                sx={{ gap: 1 }}
              >
                <PhoneIphoneOutlined
                  data-cy='phone-iphone-outlined-icon'
                  fontSize='inherit'
                />
                <Typography>{tc('device.mobile')}</Typography>
              </FlexBox>

              <NumberField
                control={control}
                disabled={cannotUpdateDomain}
                fixedHelperText
                label={t('distanceFromPageHeader.label')}
                name='distanceFromPageHeaderMobile'
                tooltip={{
                  content: t('distanceFromPageHeader.tooltip'),
                }}
                width='md'
              />

              <NumberField
                control={control}
                disabled={cannotUpdateDomain}
                fixedHelperText
                label={t('extraSpaceBelowAd.label')}
                name='extraSpaceBelowAdMobile'
                tooltip={{
                  content: t('extraSpaceBelowAd.tooltip'),
                }}
                width='md'
              />
            </BoxForm>
          </Stack>

          <Switch
            control={control}
            disabled={cannotUpdateDomain}
            label={t('excludeCreativeTypesEnabled.label')}
            labelPlacement='start'
            name='excludeCreativeTypesEnabled'
            tooltip={tooltips.excludeCreativeTypesEnabled}
          />

          {excludeCreativeTypesEnabled && (
            <TagsField
              control={control}
              disabled={cannotUpdateDomain}
              label={t('creativeCodeKeywords.label')}
              name='creativeCodeKeywords'
              width='lg'
            />
          )}
        </BoxForm>
      )}
    </>
  )
}
